import { createFileRoute } from "@tanstack/react-router";

import { SiteShell } from "@/components/marketing/site-shell";
import { LegalPage, LegalSection } from "@/components/marketing/legal-page";

const TITLE = "Privacy Policy — AROS FLOW";
const DESC =
  "What ArosFlow collects, why we collect it, how long we keep it and how to ask for your data to be deleted.";

export const Route = createFileRoute("/legal/privacy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <LegalPage title="Privacy Policy" summary={DESC}>
        <LegalSection title="Who we are">
          <p>
            ArosFlow is a real-estate deal analysis product operated by Thy Kingdom Come Property
            &amp; Realty. We are a small independent software team, not a brokerage, lender,
            appraiser or financial adviser.
          </p>
        </LegalSection>

        <LegalSection title="What we collect">
          <ul>
            <li>
              <strong>Account data:</strong> your email address and password (stored hashed by our
              authentication provider), plus a display name if you set one.
            </li>
            <li>
              <strong>Work you enter:</strong> addresses, property assumptions, seller questionnaire
              answers, deals, offers, contacts and buy boxes you save.
            </li>
            <li>
              <strong>Property data we retrieve for you:</strong> records returned by licensed
              property data providers for addresses you look up, cached so you are not charged twice
              for the same lookup.
            </li>
            <li>
              <strong>Internal product events:</strong> coarse counts such as “a landing page was
              viewed”, “an account signed up”, “a report was generated”. These carry an event name
              and your account id where you are signed in. They do not carry page URLs, browsing
              history, advertising identifiers or profiling data.
            </li>
            <li>
              <strong>Error reports:</strong> technical diagnostics when something crashes, so we
              can fix it.
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="How we use your email">
          <p>
            Your email is used to sign you in, verify your account, reset your password, send
            receipts and send product notices that affect your account. We may occasionally send
            product updates about ArosFlow itself. We do not sell your email, rent it, or pass it to
            unrelated advertisers.
          </p>
        </LegalSection>

        <LegalSection title="Seller and contact information you enter">
          <p>
            When you record a seller, buyer or partner contact, you are the controller of that
            information and you are responsible for having a lawful basis to hold and use it. We
            store consent status, permitted channels, opt-out and do-not-call flags so the product
            can respect them. ArosFlow does not send outbound messages on your behalf today.
          </p>
        </LegalSection>

        <LegalSection title="Who processes data for us">
          <ul>
            <li>Our hosting and database provider, which stores your account and saved work.</li>
            <li>Our AI provider, which processes the text you submit for analysis.</li>
            <li>Licensed property data providers, for address lookups you request.</li>
            <li>
              Our payment processor, which handles card details — we never see your card number.
            </li>
            <li>Our error monitoring provider, for crash diagnostics.</li>
          </ul>
        </LegalSection>

        <LegalSection title="Retention and deletion">
          <p>
            We keep your account and saved work for as long as your account exists. Ask us to delete
            your account and we will remove your saved deals, contacts and buy boxes, and anonymise
            the internal event counts, within 30 days — except records we must keep for tax or
            accounting purposes.
          </p>
        </LegalSection>

        <LegalSection title="Your choices">
          <p>
            You can export your deals as PDF and CSV at any time, correct anything you entered, and
            request deletion. Email us and we will act on it.
          </p>
        </LegalSection>

        <LegalSection title="Contact">
          <p>
            Privacy questions: <a href="mailto:support@arosflow.com">support@arosflow.com</a>.
          </p>
        </LegalSection>
      </LegalPage>
    </SiteShell>
  );
}
